import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { FAQ, FinalCTA } from "../../components/seo/SEOBlocks";
import { CheckCircle, ArrowRight, Info } from "lucide-react";

const PACKAGES = [
  {
    name: "Speed Build",
    weeks: "1 week",
    tagline: "Working AI-generated software",
    features: [
      "AI-generated running application",
      "Deployed on a staging URL you control",
      "2 hours of iterations after first delivery",
      "Full source code + IP transfer on final payment",
    ],
    note: "No senior-developer review is included on this package. Best for founders who need a demo yesterday and will iterate themselves.",
  },
  {
    name: "Reviewed Build",
    weeks: "2 weeks",
    tagline: "AI speed with human polish",
    features: [
      "Everything in Speed Build",
      "Senior-developer code review",
      "Bug-fixing pass across the codebase",
      "6 hours of iterations and consulting",
    ],
    highlight: true,
    note: "The most popular package. Suitable for pre-seed demos and controlled user testing.",
  },
  {
    name: "Architected Build",
    weeks: "3 weeks",
    tagline: "Production-ready data foundations",
    features: [
      "Everything in Reviewed Build",
      "Documented database data model",
      "Data-relationship diagram",
      "Structured for a future team to extend",
    ],
    note: "Best when you expect a full engineering team to inherit the codebase within the next quarter.",
  },
  {
    name: "Premium MVP",
    weeks: "4 weeks",
    tagline: "Battle-test with a real audience",
    features: [
      "Everything in Architected Build",
      "Ready for limited production release",
      "12 hours of iterations and consulting",
      "Full documentation of every feature",
    ],
    note: "For founders planning a controlled real-world validation window immediately after handover.",
  },
];

const FAQS = [
  { q: "What's the exact price of each package?", a: "Each package multiplies a per-project baseline that we calculate from the complexity of your scope. Because complexity varies enormously — a simple internal tool is not a two-sided marketplace — we don't publish generic price tags. Describe your idea in the builder and you'll get the exact number, locked, in minutes." },
  { q: "How does the complexity assessment work?", a: "We score the number of core entities, integrations, user roles, non-trivial flows and industry-specific requirements. That score maps to a baseline price. Your package choice then applies a multiplier on top. The whole calculation happens automatically the moment you submit your idea." },
  { q: "Are there any discounts?", a: "Package prices are already positioned to reflect the value delivered — for example, the one-week package is priced substantially below the three-week baseline because it includes no human review. Beyond that we don't run seasonal discounts." },
  { q: "Can I upgrade or downgrade a package mid-project?", a: "You can, up to the point where work starts. Once we're building, changes go through the change-request flow. The whole point of picking a package at the start is that both sides know what's being delivered." },
  { q: "Do you offer retainers or ongoing support?", a: "No. We're a delivery service, not a retainer shop. Once your MVP is delivered you can either extend the codebase yourself, hand it to a dev team, or come back for a follow-up delivery under a new fixed-price scope." },
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-white" data-testid="page-pricing">
      <Navbar />
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-bold uppercase tracking-widest text-zinc-700 mb-6">
            Pricing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-950 leading-[1.03]">
            Fixed packages, complexity-scored quote
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Every project uses the same simple model: pick the delivery package, and the exact price is calculated from the complexity of your scope. You see the locked number before you commit.
          </p>
        </div>
      </section>

      <section className="py-8 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pricing-packages">
          {PACKAGES.map((p) => (
            <div
              key={p.name}
              data-testid={`pricing-package-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-2xl p-6 border ${p.highlight ? "bg-zinc-950 border-zinc-950 text-white shadow-lg" : "bg-white border-zinc-200"}`}
            >
              <div className={`text-xs font-bold uppercase tracking-widest ${p.highlight ? "text-emerald-300" : "text-zinc-500"}`}>{p.weeks}</div>
              <h2 className={`text-xl font-extrabold mt-1 ${p.highlight ? "text-white" : "text-zinc-950"}`}>{p.name}</h2>
              <p className={`text-sm mt-1 mb-4 ${p.highlight ? "text-zinc-300" : "text-zinc-500"}`}>{p.tagline}</p>
              <ul className="space-y-2 mb-5">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-[13px] leading-snug ${p.highlight ? "text-zinc-200" : "text-zinc-700"}`}>
                    <CheckCircle size={12} className={`mt-0.5 flex-shrink-0 ${p.highlight ? "text-emerald-300" : "text-emerald-600"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className={`text-[11px] leading-relaxed border-t pt-3 ${p.highlight ? "text-zinc-400 border-zinc-800" : "text-zinc-500 border-zinc-100"}`}>{p.note}</p>
            </div>
          ))}
        </div>
        <div className="max-w-4xl mx-auto mt-10 bg-blue-50 border border-blue-200 rounded-2xl p-5 flex gap-3">
          <Info size={16} className="text-blue-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900 leading-relaxed">
            <strong>Why we don't publish flat prices per package.</strong> Every project is different: a two-sided marketplace has ten times the complexity of a single-user utility, even if both take four weeks. Publishing one number would mislead half of you. The builder gives you the exact number for <em>your</em> idea in minutes.
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-4 bg-zinc-50 border border-zinc-200 rounded-2xl p-5 flex gap-3" data-testid="pricing-production-scope-note">
          <Info size={16} className="text-zinc-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-700 leading-relaxed">
            <strong>MVP scope &amp; production readiness.</strong> Every package delivers a production-grade engineering foundation and architecture, intended for rapid launch and controlled real-world validation. Requirements specific to <em>unrestricted, large-scale, regulated, mission-critical, or enterprise</em> production deployment — additional security hardening, formal performance validation, operational controls, compliance validation, and infrastructure scaling — are included only when expressly defined in the agreed scope. See the <Link to="/legal" className="underline hover:text-zinc-950">Legal page</Link> for full detail.
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-8 text-center">
          <Link to="/build" data-testid="pricing-cta-build" className="inline-flex items-center justify-center px-8 py-4 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
            Get my exact price <ArrowRight size={14} className="ml-2" />
          </Link>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-50">
        <div className="max-w-4xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Payment terms</span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">How and when you pay</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-zinc-200 rounded-2xl p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Kick-off</div>
              <div className="text-2xl font-extrabold text-zinc-950 mb-1">50%</div>
              <p className="text-sm text-zinc-600 leading-relaxed">Paid via Stripe once you approve the proposal. Work begins the next business day.</p>
            </div>
            <div className="bg-white border border-zinc-200 rounded-2xl p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Delivery</div>
              <div className="text-2xl font-extrabold text-zinc-950 mb-1">50%</div>
              <p className="text-sm text-zinc-600 leading-relaxed">Paid on final delivery. IP transfers at this point. There is a fourteen-day final-review window built in.</p>
            </div>
          </div>
          <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
            Larger scopes occasionally split into three installments. Discuss during the scoping conversation if you need this.
          </p>
        </div>
      </section>

      <FAQ items={FAQS} title="Pricing — questions we hear before you commit" />
      <FinalCTA heading="See your exact price now" subheading="No sign-up, no card. Just your idea and the builder." buttonLabel="Build my prototype" />
      <Footer />
    </div>
  );
}
